import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import { FaUserSecret, FaExchangeAlt, FaExclamationTriangle, FaEnvelope } from "react-icons/fa";
import { MdMenuBook } from "react-icons/md";
import { useDispatch } from "react-redux";
import Header from "./Header";
import LeftMenu, { MenuItems } from "./LeftMenu";
import InviteModal from "../user/InviteModal";
import BroadcastMessagesForUser from "../broadcastMessage/BroadcastMessagesForUser";
import SendEmailForm from "./SendEmailFrom";
import { userActions } from "../../actions/user.actions";
import { clientService } from "../../services/client.service";
import Utils from "../../utils";

function Layout(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // The Workspace desktop replaces the sidebar's own navigation (Runbooks, Schedulers,
  // Variables, Connectors, Administration all live there as icons) — keeping it around
  // alongside Workspace would just be redundant real estate.
  const hideSidebar = location.pathname.startsWith("/workspace");

  // Fetched directly (not through clientActions/the shared `client` Redux slice) — same
  // reasoning as ClientForm.jsx: that slice is also what the Clients admin grid reads from,
  // and every logged-in user renders this Layout, so dispatching into it here would clobber
  // that grid for any Product Team user who happens to have it open. Re-fetches whenever the
  // effective client changes (impersonation, client switch).
  const [clientExpiresOn, setClientExpiresOn] = useState(null);
  const [showExpiredContactSupport, setShowExpiredContactSupport] = useState(false);

  useEffect(() => {
    if (!props.user?.clientId || !props.user?.clientGuid) {
      setClientExpiresOn(null);
      return;
    }
    let cancelled = false;
    clientService
      .get({ clientId: props.user.clientId, clientGuid: props.user.clientGuid })
      .then(
        (response) => {
          if (cancelled) return;
          setClientExpiresOn(response.data?.data?.[0]?.expiresOn ?? null);
        },
        () => {
          if (!cancelled) setClientExpiresOn(null);
        }
      );
    return () => {
      cancelled = true;
    };
  }, [props.user?.clientId, props.user?.clientGuid]);

  const isClientExpired = clientExpiresOn && new Date(clientExpiresOn) < new Date();

  const handleStopImpersonation = () => {
    dispatch(userActions.stopImpersonation(
      props.user.originalUserId,
      props.user.originalClientId,
      props.user.originalClientGuid,
      props.user.originalClientName
    ));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="overflow-hidden h-100">
      <Header user={props.user} onToggleMobileMenu={toggleMobileMenu} mobileMenuOpen={mobileMenuOpen} />

      {/* Mobile drawer overlay */}
      <div
        className={`mobile-drawer-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={closeMobileMenu}
      />

      <div className="d-flex h-100">
        {/* Desktop sidebar - always rendered except on the Workspace desktop, hidden on
            mobile via CSS the rest of the time */}
        {!hideSidebar && <LeftMenu user={props.user} />}

        {/* Mobile drawer - always rendered, hidden on desktop via CSS */}
        <div className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="d-flex flex-column flex-shrink-0 p-3 text-white bg-dark bg-gradient menu-expanded">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="fs-5">
                <MdMenuBook className="mb-2 me-3" />
                Menu
              </span>
            </div>
            <hr />
            <ul className="nav nav-pills flex-column mb-auto" data-tour="sidebar-menu" onClick={closeMobileMenu}>
              <MenuItems user={props.user} collapsed={false} />
            </ul>
          </div>
        </div>

        <div className="py-3 px-4 me-0 mb-5 overflow-auto w-100">
          {isClientExpired && (
            <Alert variant="danger" className="d-flex align-items-center justify-content-between">
              <div>
                <FaExclamationTriangle className="me-2 mb-1" size={20} />
                <strong>Your CloudSync access has expired</strong>
                <div className="mt-1">
                  <small>
                    Your account's access expired on <strong>{Utils.convertToDateOnly(clientExpiresOn)}</strong>.
                    Contact your account manager or log a support ticket to renew your access.
                  </small>
                </div>
              </div>
              <Button variant="danger" size="sm" onClick={() => setShowExpiredContactSupport(true)}>
                <FaEnvelope className="me-2" />
                Contact Support
              </Button>
            </Alert>
          )}
          {props.user?.isImpersonating && (
            <Alert variant="warning" className="d-flex align-items-center justify-content-between">
              <div>
                <FaUserSecret className="me-2 mb-1" size={20} />
                <strong>Impersonation Mode Active</strong>
                <div className="mt-1">
                  <small>
                    You are viewing the portal as <strong>{props.user.firstName} {props.user.lastName}</strong> ({props.user.email})
                    with their permissions and access rights.
                  </small>
                </div>
              </div>
              <Button
                variant="warning"
                size="sm"
                onClick={handleStopImpersonation}
              >
                <FaExchangeAlt className="me-2" />
                Exit Impersonation
              </Button>
            </Alert>
          )}
          <BroadcastMessagesForUser user={props.user} />
          <Outlet />
        </div>
      </div>
      <InviteModal user={props.user} />

      {showExpiredContactSupport && (
        <SendEmailForm
          show={showExpiredContactSupport}
          onHide={() => setShowExpiredContactSupport(false)}
          user={props.user}
          emailDetails={{
            fromMail: props.user?.email || "",
            fromName: `${props.user?.firstName || ""} ${props.user?.lastName || ""}`,
            toMail: import.meta.env.VITE_SUPPORT_EMAIL || "supportservices@armanino.com",
            subject: `CloudSync Support - ${props.user?.clientName || "Client"} access expired`,
            body: `Client: ${props.user?.clientName || ""}\nExpired On: ${Utils.convertToDateOnly(clientExpiresOn)}\n\nPlease provide additional details if needed.`,
          }}
        />
      )}
    </div>
  );
}

export default Layout;
